/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/UIContributions/GhesEndpoint/Components/OAuth2Component" />
import * as React from "react";

import { IEndpointEditorComponentProps } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/EndpointsEditorPanelInterfaces";
import { GenericAuthorizationComponent } from "DistributedTaskUI/ServiceEndpoints/Editor/Components/GenericEndpointComponents/GenericAuthorizationComponent";
import * as Resources from "DistributedTaskUI/ServiceEndpoints/Editor/Resources";
import { ServiceEndpointsSource } from "DistributedTaskUI/ServiceEndpoints/Editor/Source/ServiceEndpointsSource";
import { createOAuthConfiguration, editNewOAuthConfiguration, IGenericAuthorizationOAuthProps, IOAuthCreationProps, OAuthConfigOption, OAuthHelper } from "DistributedTaskUI/ServiceEndpoints/Editor/Utils/OAuthHelper";
import { OAuthConfigurationParams, ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import * as Constants from "DistributedTaskUI/ServiceEndpoints/Editor/Constants";
import { Button } from "VSSUI/Button";
import { ButtonGroup } from "VSSUI/ButtonGroup";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Link } from "VSSUI/Link";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { TextField } from "VSSUI/TextField";
import { DropdownSelection } from "VSSUI/Utilities/DropdownSelection";

import { FormatComponent } from "VSS/Platform/Components/Format";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

/**
 * OAuth2Component is currently only used by Github Enterprise (UIContributions).
 * Other types use the general OAuth code.
 */
export interface IOAuth2ComponentState {
    showCreateOauthForm: boolean;
    showOAuthCreatedView: boolean;
    isEditingOAuth: boolean;
    savedOAuthName?: string;
    errorDictionary?: { [key: string]: string };
    newlyCreatedConfigId?: string;
    /**
     * Whether we are only updating the name of the newly created oauth configuration as an update API call or
     * if we are updating the whole configuration as a delete and recreate operation.
    */
    shouldUpdateWholeOAuthConfig: boolean;
    createOAuthFormState: OAuthConfigurationParams;
}

export interface IOAuth2ComponentProps extends IEndpointEditorComponentProps {
    invalidComponentCount: number;
    updateInvalidComponentCount: (count: number) => void;
    hasConfigurations: boolean;
    serviceEndpointComponent: JSX.Element;
    oAuthHelper: OAuthHelper;
}

export class OAuth2Component extends VssComponent<IOAuth2ComponentProps, IOAuth2ComponentState> {
    private readonly _generalError = "generalError";
    private readonly _oAuthNameId = "Name";
    private readonly _oAuthEndpointTypeId = "EndpointType";
    private readonly _oAuthUrlId = "Url";
    private readonly _oAuthClientIdId = "ClientId";
    private readonly _oAuthClientSecretId = "ClientSecret";
    private _selection = new DropdownSelection();
    private _isCreateButtonDisabled: boolean;
    private _draftServiceEndpoint: ServiceEndpoint;
    private _source: ServiceEndpointsSource;
    constructor(props: IOAuth2ComponentProps, context: IVssComponentContext) {
        super(props, context);
        this._initializeValues(context);
    }

    public componentWillUnmount() {
        this.setState(this._getCreateOAuthState());
        this._draftServiceEndpoint = this.props.draftEndpoint;
    }

    public componentDidUpdate(prevProps: Readonly<IOAuth2ComponentProps>, prevState: Readonly<IOAuth2ComponentState>): void {
        if (prevProps.hasConfigurations !== this.props.hasConfigurations) {
            const expectedAuthMethod = this.props.hasConfigurations ? OAuthConfigOption.ExistingOAuth : OAuthConfigOption.CreateOAuth;
            if (this.props.oAuthHelper.getOAuthConfigOption() !== expectedAuthMethod) {
                this._selectExistingOrNewOAuthMethod(expectedAuthMethod);
            }
        }

        if (prevState.isEditingOAuth !== this.state.isEditingOAuth) {
            if (this.state.isEditingOAuth) {
                this.props.updateInvalidComponentCount(1);
            } else {
                this.props.updateInvalidComponentCount(-1);
            }
        }
    }

    public render(): React.ReactNode {
        const isCreateOAuthFormVisible = this._isCreateOAuthSelection() && !!this.state.showCreateOauthForm;
        const oAuthFormClass = isCreateOAuthFormVisible ? " oauth-form" : "";
        const oAuthCreatedView = this.state.showOAuthCreatedView && !this.state.isEditingOAuth;
        const oAuthCreatedClass = oAuthCreatedView ? " oauth-created" : "";
        return (
            <div className="oauth2-panel">
                {this._getOAuthRadioContent()}
                {this._isCreateOAuthSelection()
                    ? <div className={"create-new-oauth" + oAuthFormClass + oAuthCreatedClass}>
                        {this._getNewlyCreatedOAuthFormContent()}
                        {this._getCreateOAuthContent()}
                        {this._getInputDescriptorsComponent()}
                        {this._getCreateServiceConnectionContent()}
                        {this._getNewOAuthError()}
                    </div>
                    : <div className="existing-oauth">
                        {this._getInputDescriptorsComponent()}
                        {this._getCreateServiceConnectionContent()}
                    </div>
                }
            </div>
        );
    }

    private _initializeValues(context: IVssComponentContext) {
        this._source = ServiceEndpointsSource.getInstance(context.pageContext);
        this._draftServiceEndpoint = this.props.draftEndpoint;
        const expectedAuthMethod = this.props.hasConfigurations ? OAuthConfigOption.ExistingOAuth : OAuthConfigOption.CreateOAuth;
        this.props.oAuthHelper.updateOAuthConfigOption(expectedAuthMethod);

        this.state = this._getCreateOAuthState();
        this._isCreateButtonDisabled = true;
        this._selection.select(0);
    }

    private _getOAuthButtons(oAuthCreationProps: IOAuthCreationProps, authorizeCallback: (configId?: string) => void): JSX.Element {
        const isEditingNewOauth = this.state.showCreateOauthForm && this.state.isEditingOAuth;

        return (
            <ButtonGroup className="create-oauth-buttons">
                <Button
                    className="create-save-button"
                    disabled={this._isCreateButtonDisabled}
                    text={isEditingNewOauth ? Resources.SaveAndAuthorizeText : Resources.CreateText}
                    onClick={() => {
                        isEditingNewOauth
                            ? editNewOAuthConfiguration(
                                this._draftServiceEndpoint,
                                this._source,
                                (state: ServiceEndpoint) => this.props.updateDraftEndpointState(state),
                                (configId?: string) => authorizeCallback(configId),
                                oAuthCreationProps,
                                this.state.createOAuthFormState
                                )
                            : createOAuthConfiguration(
                                this._draftServiceEndpoint,
                                this._source,
                                this.state.shouldUpdateWholeOAuthConfig,
                                (state: ServiceEndpoint) => this.props.updateDraftEndpointState(state),
                                (configId?: string) => authorizeCallback(configId),
                                oAuthCreationProps,
                                this.state.createOAuthFormState
                                );
                    }}
                    primary
                />
                {
                    isEditingNewOauth
                    &&  <Button
                        className="create-save-button"
                        text={Resources.CancelEditing}
                        onClick={() => {
                            this.state.createOAuthFormState.name = this.state.savedOAuthName;
                            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
                            this.setState({
                                showCreateOauthForm: false,
                                showOAuthCreatedView: true,
                                isEditingOAuth: false,
                                shouldUpdateWholeOAuthConfig: false
                            });
                        }}
                    />
                }
            </ButtonGroup>
        );
    }

    private _getInputDescriptorsComponent() {
        let oAuthProps: IGenericAuthorizationOAuthProps = {
            oAuthHelper: this.props.oAuthHelper
        };
        if (this._isCreateOAuthSelection()) {
            const oAuthCreationProps: IOAuthCreationProps = {
                oAuthCreatedAndAuthorizedCallback: (configId?: string, error?: string, editShouldDelete?: boolean) => this._updatePanelStateAfterOauthCreation(configId, error, editShouldDelete),
                shouldUpdateWholeOAuthConfig: this.state.shouldUpdateWholeOAuthConfig,
                newlyCreatedOauthConfigId: this.state.newlyCreatedConfigId
            };
            oAuthProps.buttonComponent =  (authorizeCallback: (configId?: string) => void) => this._getOAuthButtons(oAuthCreationProps, authorizeCallback),
            oAuthProps.oAuthCreatedAndAuthorizedCallback = (configId?: string, error?: string, editShouldDelete?: boolean) => this._updatePanelStateAfterOauthCreation(configId, error, editShouldDelete),
            oAuthProps.createdOAuthUrl = this.state.createOAuthFormState.url;
        }

        return <div className="new-oauth-input-descriptors-container">
            <GenericAuthorizationComponent
                serviceEndpointType={this.props.serviceEndpointType}
                draftEndpoint={this.props.draftEndpoint}
                isUpdateForm={this.props.isUpdateForm}
                updateDraftEndpointState={this.props.updateDraftEndpointState}
                updateInvalidInputsCount={(count) => this._isCreateOAuthSelection() ? null : this.props.updateInvalidInputsCount(count)}
                oAuthProps={oAuthProps}
                disableAllPipelinesSetting={this.props.disableAllPipelinesSetting}
            />
        </div>;
    }

    private _getOAuthRadioContent(): JSX.Element {
        return (
            <div className="default-input-margin-top">
                <RadioButtonGroup
                    text={Resources.OAuthConfiguration}
                    selectedButtonId={this.props.oAuthHelper.getOAuthConfigOption().toString()}
                    onSelect={(id) => this._selectExistingOrNewOAuthMethod(
                        id === OAuthConfigOption.ExistingOAuth.toString() ? OAuthConfigOption.ExistingOAuth : OAuthConfigOption.CreateOAuth,
                        true)}>
                    <RadioButton
                        id={OAuthConfigOption.ExistingOAuth.toString()}
                        text={Resources.OAuthConfiguration_SelectExisting}
                        disabled={!this.props.hasConfigurations} />
                    <RadioButton id={OAuthConfigOption.CreateOAuth.toString()}>
                        {!this.state.showOAuthCreatedView && !this.state.isEditingOAuth && <span className="bolt-radio-button-label">{Resources.OAuthConfiguration_CreateNew}</span>}
                        {this.state.showOAuthCreatedView && !this.state.isEditingOAuth
                            && <FormatComponent className="bolt-radio-button-label" format={Resources.OAuthConfiguration_UseNew}>
                                <Link onClick={() => this._onEditLinkClick()}>
                                    {Resources.Edit}
                                </Link>
                            </FormatComponent>}
                        {this.state.isEditingOAuth && <span className="bolt-radio-button-label">{Resources.OAuthConfiguration_EditNew}</span>}
                    </RadioButton>
                </RadioButtonGroup>
            </div>);
    }

    private _onEditLinkClick() {
        this.setState({
            showCreateOauthForm: true,
            showOAuthCreatedView: false,
            isEditingOAuth: true,
            errorDictionary: {}
        });
    }

    private _selectExistingOrNewOAuthMethod(oAuthConfigOption: OAuthConfigOption, update: boolean = false) {
        if (this.props.invalidComponentCount === 0) {
            this.props.oAuthHelper.updateServiceEndpointComponentIsInvalid(false);
        }

        if (oAuthConfigOption === this.props.oAuthHelper.getOAuthConfigOption()) {
            return;
        }

        const newState: IOAuth2ComponentState = this._getCreateOAuthState();
        if (oAuthConfigOption === OAuthConfigOption.CreateOAuth) {
            newState.showCreateOauthForm = true;
            this._isCreateButtonDisabled = true;
        }

        this.props.oAuthHelper.updateOAuthConfigOption(oAuthConfigOption);
        this._draftServiceEndpoint.url = null;
        this._draftServiceEndpoint.authorization.parameters["AccessToken"] = null; // this undoes previous authorization
        if (update) {
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
            this.setState(newState);
        }

        if (!this.props.isUpdateForm) {
            const value = this.props.invalidComponentCount === 0 ? 0 : undefined;
            this.props.updateInvalidComponentCount(value);
        }
    }

    private _textfieldOnChange(value: string, isCreateButtonDisabledForEditForm: boolean, formId: string, shouldUpdateWholeOAuthConfig: boolean) {
        if (!this.state.isEditingOAuth) {
            this._enableCreateButtonIfFormIsFilled();
        } else {
            this._isCreateButtonDisabled = isCreateButtonDisabledForEditForm;
        }

        let state = this.state;
        switch (formId) {
            case this._oAuthNameId:
                state.createOAuthFormState.name = value;
                break;
            case this._oAuthUrlId:
                state.createOAuthFormState.url = value;
                break;
            case this._oAuthClientIdId:
                state.createOAuthFormState.clientId = value;
                break;
            case this._oAuthClientSecretId:
                state.createOAuthFormState.clientSecret = value;
                break;
            default:
                break;
        }
        this.setState(state);

        if (shouldUpdateWholeOAuthConfig && !this.state.shouldUpdateWholeOAuthConfig) {
            this.setState({shouldUpdateWholeOAuthConfig: true});
        }
    }

    private _getCreateOAuthContent() {
        if (!this.state.isEditingOAuth) {
            this._enableCreateButtonIfFormIsFilled();
        }
        if (this.state.showCreateOauthForm) {
            return <div className="create-oauth-form">
                {this._getFormInputComponent(
                    this._oAuthNameId, Resources.Name, this.state.createOAuthFormState.name, false, false, (value) => {
                        this._textfieldOnChange(
                            value,
                            this.state.savedOAuthName === value ? true : false,
                            this._oAuthNameId,
                            false
                        );
                    })}
                {this._getFormInputComponent(this._oAuthEndpointTypeId, Resources.SourceType, Resources.GithubEnterpriseServerValue, true)}
                {this._getFormInputComponent(this._oAuthUrlId, Resources.GithubEnterpriseServerUrl, this.state.createOAuthFormState.url, false, false, (value) => {
                    this._textfieldOnChange(
                        value,
                        false,
                        this._oAuthUrlId,
                        true
                    );
                    })}
                {this._getFormInputComponent(this._oAuthClientIdId, Resources.ClientId, this.state.createOAuthFormState.clientId, false, false, (value) => {
                        this._textfieldOnChange(
                            value,
                            false,
                            this._oAuthClientIdId,
                            true
                        );
                    })}
                {this._getFormInputComponent(this._oAuthClientSecretId, Resources.ClientSecret, this.state.createOAuthFormState.clientSecret, false, true, (value) => {
                        this._textfieldOnChange(
                            value,
                            false,
                            this._oAuthClientSecretId,
                            true
                        );
                    })}
            </div>;
        }
    }

    /**
     * Creating components with React instead of using the pure input descriptors.
     * Using the input descriptors to store values.
     */
    private _getFormInputComponent(formItemId: string, label: string, value: string, isDisabled: boolean, isConfidential?: boolean, onChange?: (value: string) => void): JSX.Element {
        const error = !!this.state.errorDictionary[formItemId];
        return (
            <FormItem
                className="default-input-margin-top"
                label={label}
                message={error && this.state.errorDictionary[formItemId]}
                error={error}>
                    {
                        formItemId === this._oAuthEndpointTypeId
                        ? <Dropdown
                            selection={this._selection}
                            disabled={true}
                            items={[value]}
                        />
                        : <TextField
                            key={formItemId}
                            value={this.state.isEditingOAuth && isConfidential ? "****************************************" : value}
                            onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, value: string) => {
                                if (!!onChange) {
                                    onChange(value);
                                }
                            }}
                            inputType={isConfidential ? "password" : "text"}
                            disabled={isDisabled}
                        />
                    }
            </FormItem>
        );
    }

    private _getCreateServiceConnectionContent() {
        // Always show service endpoint creation content if we are updating the service connection by clicking edit in service endpoint details page.
        // When creating a new service endpoint, show service endpoint content once oauth configuration has been authorized.
        if (!this.props.isUpdateForm) {
            if (!this._isCreateOAuthSelection() && !this._draftServiceEndpoint.authorization.parameters[Constants.AccessToken]) {
                return;
            }

            if (this._isCreateOAuthSelection() && this.state.showCreateOauthForm) {
                return;
            }
        }
        return this.props.serviceEndpointComponent;
    }

    private _getNewOAuthError() {
        if (this.state.showOAuthCreatedView) {
            return;
        }

        return !!this.state.errorDictionary[this._generalError] &&
            <div className="auth-validation-error-content error-content-wrap">
                {this.state.errorDictionary[this._generalError]}
            </div>;
    }

    private _getNewlyCreatedOAuthFormContent() {
        if (this.state.showOAuthCreatedView && !this.state.isEditingOAuth) {
            return (
                <TextField
                    className="created-oauth-name"
                    disabled={true}
                    value={this.state.createOAuthFormState.name} />
            );
        }
    }

    private _isCreateOAuthSelection() {
        return this.props.oAuthHelper.getOAuthConfigOption() === OAuthConfigOption.CreateOAuth;
    }

    private _updatePanelStateAfterOauthCreation(configId?: string, error?: string, editShouldDelete: boolean = false) {
        let showCreateOauthForm = this.state.showCreateOauthForm;
        let showOAuthCreatedView = this.state.showOAuthCreatedView;
        let isEditingOAuth = this.state.isEditingOAuth;
        let errorDictionary = this.state.errorDictionary;
        let savedOAuthName = this.state.savedOAuthName;
        let shouldUpdateWholeOAuthConfig = this.state.shouldUpdateWholeOAuthConfig;
        if (configId) {
            if (!error) {
                showCreateOauthForm = false;
                showOAuthCreatedView = true;
                shouldUpdateWholeOAuthConfig = false;
            }
            isEditingOAuth = false;
            errorDictionary = {};
            savedOAuthName = this.state.createOAuthFormState.name;
            this.props.updateDraftEndpointState(this._draftServiceEndpoint);
        }

        if (error) {
            if (this.state.isEditingOAuth) {
                if (error.includes("OAuth configuration with name") && error.includes("already exists")) {
                    errorDictionary[this._oAuthNameId] = error;
                } else {
                    errorDictionary[this._generalError] = error;
                }
            }
            else {
                if (error.includes("configuration.Url")) {
                    errorDictionary[this._generalError] = Resources.InvalidServerURLMessage;
                } else if (error.includes("OAuth configuration with name") && error.includes("already exists")) {
                    errorDictionary[this._generalError] = error;
                } else {
                    errorDictionary[this._generalError] = error;
                }
            }

            this._isCreateButtonDisabled = true;
        }

        if (editShouldDelete) {
            shouldUpdateWholeOAuthConfig = true;
        }

        this.setState({
            showCreateOauthForm: showCreateOauthForm,
            showOAuthCreatedView: showOAuthCreatedView,
            isEditingOAuth: isEditingOAuth,
            savedOAuthName: savedOAuthName,
            errorDictionary: errorDictionary,
            newlyCreatedConfigId: configId,
            shouldUpdateWholeOAuthConfig: shouldUpdateWholeOAuthConfig
        });
    }

    private _getCreateOAuthState(): IOAuth2ComponentState {
        return {
            showCreateOauthForm: this._isCreateOAuthSelection() ? true : false,
            showOAuthCreatedView: false,
            isEditingOAuth: false,
            savedOAuthName: null,
            errorDictionary: {},
            newlyCreatedConfigId: null,
            shouldUpdateWholeOAuthConfig: false,
            createOAuthFormState: {
                name: "",
                url: "",
                endpointType: "githubenterprise",
                clientId: "",
                clientSecret: ""
            }
        };
    }

    private _enableCreateButtonIfFormIsFilled() {
        if (!this.state.isEditingOAuth
            && this._isCreateButtonDisabled
            && this.state.createOAuthFormState.name && this.state.createOAuthFormState.name.length > 0
            && this.state.createOAuthFormState.url && this.state.createOAuthFormState.url.length > 0
            && this.state.createOAuthFormState.clientId && this.state.createOAuthFormState.clientId.length > 0
            && this.state.createOAuthFormState.clientSecret && this.state.createOAuthFormState.clientSecret.length > 0) {
            this._isCreateButtonDisabled = false;
        } else {
            this._isCreateButtonDisabled = true;
        }
    }
}
